import { queryOptions, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { apiGet } from './client'
import { acquirePortalLink, type PortalEvent, type PortalStatus } from './portalSocket'
import { realtimeConfigQuery } from './realtime'

/**
 * CE VINE DE PE O MASINA VIE, si carligul care se aboneaza si se dezaboneaza
 * singur.
 *
 * Perechea veche: `Widgets.liveHealth` din `public/js/easeedesk/easeedesk.js:6500`,
 * cu `ioSocket.requestPerformance/Diskspace/Processes` la trei secunde
 * (`easeedesk.js:6874-6894`).
 *
 * Nu e al lui Easeedesk. Fisa unui aparat si tabloul de alarme se uita la
 * aceleasi cifre, deci sta aici, langa client, nu langa primul ecran care l-a
 * folosit.
 */

/** Un fir de procesor: `{Name: "0", Usage: 33}`. `Name` vine si ca numar. */
export interface LiveCpuThread {
  Name: string | number
  Usage: number | string
}

/** Un proces, cum il trimite masina: `processes.app[]` si `processes` (evenimentul). */
export interface LiveProcess {
  name: string
  cpuLoad: number
  memory: number
}

/** O fereastra deschisa. `foreground` e cea din fata, cu acelasi `PID`. */
export interface LiveApplication {
  Title?: string
  PID?: number
}

/** O conexiune stabilita: `address` e "ip:port". */
export interface LiveConnection {
  address: string
  protocol?: string
}

/**
 * Raspunsul evenimentului `performance`.
 *
 * Numele sunt ale MASINII, nu ale noastre - inclusiv `connectionsEsdtablished`,
 * scris gresit acolo si citit asa in portal de ani de zile
 * (`easeedesk.js:6987`). Corectat aici, campul n-ar mai fi gasit niciodata.
 *
 * Totul e optional: agentul trimite ce a putut citi, si o versiune mai veche de
 * agent trimite mai putin.
 */
export interface LivePerformance {
  cpuLoad?: number
  memoryLoad?: number
  swapLoad?: number
  cpuUsage?: LiveCpuThread[]
  totalMemory?: number
  memoryFree?: number
  /** In MEGAOCTETI, spre deosebire de `totalMemory`, care e in octeti. */
  totalSwap?: number
  swapFree?: number
  discReadsPerSecond?: number
  discWritesPerSecond?: number
  diskReadBytesPerSec?: number
  diskWriteBytesPerSec?: number
  bandwidth?: number
  bytesReceivedperSec?: number
  bytesSentperSec?: number
  foreground?: LiveApplication
  applications?: LiveApplication[]
  connectionsEsdtablished?: LiveConnection[]
  processes?: { app?: LiveProcess[] }
}

/** Raspunsul evenimentului `processes`. */
export interface LiveProcesses {
  app?: LiveProcess[]
}

export interface DeviceLive {
  status: PortalStatus
  /** `CHECK_DEVICE`. null = inca nu s-a intrebat. */
  online: boolean | null
  /** Ultimul raspuns al fiecarui eveniment cerut. */
  data: Partial<Record<PortalEvent, unknown>>
  /** Cand a venit ultimul raspuns, oricare. 0 = niciunul inca. */
  at: number
}

/** Ca in portal: `setInterval(..., 3000)`. */
export const LIVE_EVERY_MS = 3000

/**
 * Se aboneaza la o masina cat sta ecranul deschis, si se dezaboneaza cand se
 * inchide.
 *
 * `events` se citeste o singura data pe montare, dupa continut - o lista scrisa
 * in linie la fiecare randare ar reface abonamentul de trei ori pe secunda.
 */
export function useDeviceLive(
  device: number,
  events: PortalEvent[],
  everyMs: number = LIVE_EVERY_MS,
  /** Campuri in plus in fiecare cerere (`email` la `domain`). Se citeste o data pe montare, ca `events`. */
  extra?: Record<string, string>
): DeviceLive {
  const { data: config } = useQuery(realtimeConfigQuery)
  const [status, setStatus] = useState<PortalStatus>('connecting')
  const [online, setOnline] = useState<boolean | null>(null)
  const [data, setData] = useState<Partial<Record<PortalEvent, unknown>>>({})
  const [at, setAt] = useState(0)

  const wanted = events.join(',')
  const extraJson = JSON.stringify(extra ?? {})

  useEffect(() => {
    if (config === undefined || device <= 0) {
      return
    }

    const asked = wanted.split(',').filter((e) => e !== '') as PortalEvent[]
    const link = acquirePortalLink(config)
    const off: Array<() => void> = []

    setStatus(link.status)
    off.push(link.watch(setStatus))

    for (const event of asked) {
      off.push(
        link.on(event, device, (payload) => {
          setData((old) => ({ ...old, [event]: payload }))
          setAt(Date.now())
        })
      )
    }

    const round = () => {
      /* `ask` nu face nimic cat legatura nu e gata, deci nu se pierde nimic
         daca primul tur cade inainte de `authenticated`: al doilea prinde. */
      const fields = JSON.parse(extraJson) as Record<string, string>
      for (const event of asked) {
        link.ask(event, device, fields)
      }
      link.check(device, setOnline)
    }

    round()
    const timer = setInterval(round, everyMs)

    return () => {
      clearInterval(timer)
      for (const stop of off) {
        stop()
      }
      /* ASTA e randul care nu are voie sa lipseasca: fara el, conexiunea si
         cererea la trei secunde raman dupa ce omul a plecat de pe ecran. */
      link.release()
    }
  }, [config, device, wanted, everyMs, extraJson])

  return { status, online, data, at }
}

/**
 * RAMA panoului: ce se stie din baza inainte sa raspunda masina.
 *
 * Contractul lui `GET /api/v2/devices/{id}/live-frame` (v4/api,
 * App\Devices\Live\DeviceLiveFrame). Perechea veche: ce lua
 * `Easeedesk::live()` din baza si scria in macheta.
 *
 * Memoria e in OCTETI (in baza sta in kiloocteti; inmultirea o face API-ul).
 */
export interface DeviceLiveFrame {
  device: number
  cpu: {
    model: string
    /** Cate randuri de fir se deseneaza. 0 = nu se stie, deci niciunul. */
    threads: number
    /** Ultima incarcare STIUTA: acul porneste de acolo, nu de la zero. */
    usage: number
  }
  memory: {
    ram: number | null
    ram_free: number | null
    swap: number | null
    swap_free: number | null
  }
  storage: {
    size: number | null
    type: string
  }
}

export function deviceLiveFrameQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'live-frame'],
    queryFn: () => apiGet<DeviceLiveFrame>(`/devices/${id}/live-frame`),
  })
}
